import { Box } from '@material-ui/core';
import { OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import { SectionTitle } from '@/components/modules/Application/components/_shared/components/Section/SectionTitle';
import { useMerchantPosContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/contexts/MerchantPosContext';
import { BorrowerOnboardingDetailsForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/BorrowerOnboardingDetailsForm';
import { BorrowerOnboardingDetailsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/maps/borrowerOnboardingDetails.map';
import {
  BorrowerOnboardingDetailsFormSchema,
  BorrowerOnboardingDetailsFormType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/borrowerOnboardingDetailsForm.schema';
import { ParticipantCards } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/ParticipantCards/ParticipantCards';
import { MERCHANT_POS_DEFAULT_VALUES } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/constants/MerchantPosDefaultValues';
import { useMerchantPos } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/hooks/useMerchantPos';
import { useMerchantPosDefaultValues } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/hooks/useMerchantPosDefaultValues';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { developOptions } from '@/config/developOptions';

export const MerchantPosForm = () => {
  const { selectedRole, borrowerOnboardingDetails, isEditing, paymentMethodDetailEntity } = useMerchantPosContext();
  const { additionalProfessionalDetailsRequired, isCompanyCifRequired } = useMerchantPos();

  const { getTextIdTranslation: t } = useTranslation();
  const { defaultValues, getDefaultValues } = useMerchantPosDefaultValues();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getDefaultValues().finally(() => setIsLoading(false));
  }, [selectedRole]);

  const schema = () =>
    BorrowerOnboardingDetailsFormSchema(
      t,
      borrowerOnboardingDetails,
      selectedRole,
      paymentMethodDetailEntity.requiresNieData(),
      additionalProfessionalDetailsRequired,
      isCompanyCifRequired,
    );

  if (isLoading) {
    return <FormSkeleton />;
  }

  const title = !selectedRole ? 'INTERVENERS' : isEditing ? 'EDIT_DATA' : `ADD_${selectedRole}`;

  return (
    <Section>
      <SectionTitle
        title={title}
        subtitle={selectedRole ? '' : t('FILL_IN_INTERVENERS_DATA')}
        subtitleDynamicDataTexts={selectedRole && [t(selectedRole)]}
      />
      <FormWrapper formSchema={schema} defaultValues={defaultValues}>
        <Form defaultValues={defaultValues} />
      </FormWrapper>
    </Section>
  );
};

interface FormProps {
  defaultValues?: BorrowerOnboardingDetailsMap;
}

export const Form = ({ defaultValues }: FormProps) => {
  const { selectedRole, borrowerOnboardingDetails, paymentMethodDetailEntity, update } = useMerchantPosContext();

  const selectedBorrowerOnboardingDetails = borrowerOnboardingDetails.find((p) => p.role === selectedRole);

  const {
    formState: { errors, dirtyFields },
    reset,
  } = useFormContext<BorrowerOnboardingDetailsFormType>();

  const numberOfDirtyFields = Object.values(dirtyFields).length;

  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    update({ disableSaveButton: !numberOfDirtyFields });
  }, [numberOfDirtyFields]);

  useEffect(() => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const element = document.getElementsByName(errorFields[0])[0];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [errors]);

  useEffect(() => {
    // If no role is selected, we reset the form
    const isBorrower = selectedRole === OnboardingDetailsRole.BORROWER;
    const customer = isBorrower ? paymentMethodDetailEntity.checkoutSession.customer : undefined;
    const blankForm = BorrowerOnboardingDetailsMap.getBlankForm(customer);

    if (selectedRole === undefined) {
      reset(blankForm);
      return;
    }

    if (selectedBorrowerOnboardingDetails) {
      reset(BorrowerOnboardingDetailsMap.fromStoredToForm(selectedBorrowerOnboardingDetails));
      return;
    }

    // To prevent the form from being reset on initial load
    if (isBorrower && paymentMethodDetailEntity.hasOnboardingDetails(OnboardingDetailsRole.BORROWER)) {
      return;
    }

    if (developOptions.USE_FAKE_FORMS_AS_DEFAULT_VALUES) {
      const defaultValues = {
        ...MERCHANT_POS_DEFAULT_VALUES[selectedRole],
        ...(blankForm.firstName !== '' && { firstName: blankForm.firstName }),
        ...(blankForm.lastName !== '' && { lastName: blankForm.lastName }),
        ...(blankForm.documentNumber !== '' && { documentNumber: blankForm.documentNumber }),
        ...(blankForm.email !== '' && { email: blankForm.email }),
        ...(blankForm.phoneNumber !== '' && { phoneNumber: blankForm.phoneNumber }),
        ...(blankForm.phoneNumber !== '' && { dialCode: blankForm.dialCode }),
      };
      reset(defaultValues);
      return;
    }

    reset(blankForm);
  }, [selectedRole, selectedBorrowerOnboardingDetails]);

  if (!selectedRole && borrowerOnboardingDetails.length === 0) {
    return null;
  }

  if (selectedRole) {
    return defaultValues ? <BorrowerOnboardingDetailsForm /> : <FormSkeleton />;
  }

  return <ParticipantCards />;
};

const FormSkeleton = () => {
  return (
    <CustomPaper fill="paper" style={{ padding: 24 }}>
      <Box display="flex" flexDirection="column" gridGap={24}>
        <CustomSkeleton height={84} />
        <CustomSkeleton height={2000} />
      </Box>
    </CustomPaper>
  );
};
